<template>
    <div class="killerBg relative flex">
        <KillerSplashHost ref="splashHost" @main-menu="$emit('exit')" />
        <div
            class="flex h-full w-full flex-col items-center justify-between bg-[url('@/assets/images/games/killer/boardPattern.svg')] bg-size-[auto_100%] bg-center p-4"
        >
            <Board :sections="sections" />

            <div class="flex flex-col items-center justify-center" v-if="currentPlayer">
                <h2 class="font-title leading-3 font-bold uppercase">{{ $t('play.your_turn') }}</h2>
                <h2
                    class="font-title font-bold uppercase"
                    :style="{ color: getProfileColor(currentPlayer?.id) }"
                >
                    {{ currentPlayer?.tag_name }}
                </h2>
                <p
                    v-if="isCurrentPlayerKiller || pointsToKiller === 0"
                    class="text-titles mt-2 max-w-[60%] text-center text-sm uppercase"
                    style="line-height: 1.2"
                >
                    {{ $t('killer.killer_instructions') }}
                </p>
                <p
                    v-else
                    class="text-titles mt-2 max-w-[60%] text-center text-sm uppercase"
                    style="line-height: 1.2"
                    data-testid="killer-points-to-killer"
                >
                    {{ $t('killer.points_to_killer', pointsToKiller) }}
                </p>
            </div>

            <KillerPlayerList
                v-if="currentPlayer"
                :current-player="currentPlayer"
                :profiles="profiles"
                :all-profile-states="allProfileStates"
                :get-profile-color="getProfileColor"
                @confirm-selection="onTargetConfirmed"
                @reset-selection="onTargetReset"
                @became-killer="onBecameKiller"
                @player-eliminated="showEliminatedSplash"
                @status-points-change="currentStatusPoints = $event"
            />
        </div>
    </div>
</template>

<script>
import Board from '@/components/games/killer/Board.vue'
import KillerPlayerList from '@/components/games/killer/KillerPlayerList.vue'
import KillerSplashHost from '@/components/games/killer/splash/KillerSplashHost.vue'
import { MAX_STATUS_POINTS } from '@/components/games/killer/killerConstants.js'
import { useMatchStore } from '@/stores/match.js'

export default {
    name: 'KillerGame',
    components: {
        Board,
        KillerPlayerList,
        KillerSplashHost,
    },
    emits: ['exit'],
    data() {
        return {
            playerColors: ['#0EC1FF', '#FF0000', '#C000FF', '#1AD200'],
            killerSplashShownFor: null,
            currentStatusPoints: 0,
        }
    },
    mounted() {
        console.log('KillerGame component mounted', this.matchStore.getProfiles)
        if (this.isMatchOver) {
            this.showWinSplash()
            return
        }
        this.$refs.splashHost.show('start', { players: this.profiles.map(this.withColor) })
        this.showTurnSplash()
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        sections() {
            return this.generateSections()
        },
        allProfileStates() {
            const matchData = this.matchStore.getMatchData
            if (!matchData?.sets?.length) return []

            const currentSet = matchData.sets[matchData.sets.length - 1]
            if (!currentSet?.legs?.length) return []

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return []

            const lastTurn = currentLeg.turns[currentLeg.turns.length - 1]
            return lastTurn.team_states || []
        },
        currentPlayerState() {
            if (!this.currentPlayer) return null
            return this.allProfileStates.find((state) => state.team === this.currentPlayer.team)
        },
        isCurrentPlayerKiller() {
            return this.currentPlayerState?.is_killer || false
        },
        pointsToKiller() {
            return Math.max(0, MAX_STATUS_POINTS - this.currentStatusPoints)
        },
        isMatchOver() {
            return this.matchStore.isMatchOver
        },
    },
    methods: {
        quitMatch() {
            console.log('Quitting match, clearing match data')
            this.matchStore.clearMatch()
            this.$router.push({ name: 'games' })
        },
        generateSections() {
            const sections = {}
            const dartboardOrder = [
                3, 17, 2, 15, 10, 6, 13, 4, 18, 1, 20, 5, 12, 9, 14, 11, 8, 16, 7, 19,
            ]

            dartboardOrder.forEach((sectionNumber, dartboardIndex) => {
                const claimedBy = this.allProfileStates.find(
                    (state) => state.number === sectionNumber,
                )

                if (claimedBy && (claimedBy.lives || 0) > 0) {
                    let sectionColor = '#FFFFFF'
                    let sectionAvatar = null

                    const owner = this.profiles.find((p) => p.team === claimedBy.team)
                    if (owner) {
                        sectionColor = this.getProfileColor(owner.id)
                        sectionAvatar = owner.avatar
                    }

                    sections[sectionNumber] = {
                        angle: dartboardIndex * 18,
                        hits: claimedBy?.hits_on_own_target || 0,
                        color: sectionColor,
                        avatar: sectionAvatar,
                        isKiller: claimedBy?.is_killer || false,
                        lives: claimedBy?.lives || 0,
                    }
                }
            })

            return sections
        },
        onTargetConfirmed() {
            console.log('✅ Target/darts confirmed and submitted')
        },
        onBecameKiller() {
            this.killerSplashShownFor = this.currentPlayer?.id
            this.showBecameKillerSplash()
        },
        onTargetReset() {
            console.log('🔄 Target selection reset')
        },
        getProfileColor(profileId) {
            const profileIndex = this.profiles.findIndex((p) => p.id === profileId)
            return this.playerColors[profileIndex % this.playerColors.length] || '#FFFFFF'
        },
        withColor(player) {
            return { ...player, color: this.getProfileColor(player.id) }
        },
        showTurnSplash() {
            if (!this.currentPlayer || this.isMatchOver) return
            this.$refs.splashHost.show('turn', {
                player: this.currentPlayer,
                color: this.getProfileColor(this.currentPlayer.id),
                targetNumber: this.currentPlayerState?.number ?? null,
                isKiller: this.isCurrentPlayerKiller,
            })
        },
        showBecameKillerSplash() {
            if (!this.currentPlayer) return
            this.$refs.splashHost.show('becameKiller', {
                player: this.currentPlayer,
                color: this.getProfileColor(this.currentPlayer.id),
            })
        },
        showEliminatedSplash(player) {
            this.$refs.splashHost.show('eliminated', {
                player,
                color: this.getProfileColor(player.id),
            })
        },
        showWinSplash() {
            const winner = this.profiles.find(
                (player) =>
                    this.allProfileStates.find((state) => state.team === player.team)?.lives > 0,
            )
            if (!winner) {
                this.$emit('exit')
                return
            }
            this.$refs.splashHost.show('win', {
                player: winner,
                color: this.getProfileColor(winner.id),
                targets: this.profiles
                    .filter((player) => player.id !== winner.id)
                    .map(this.withColor),
            })
        },
    },
    watch: {
        isCurrentPlayerKiller() {
            if (!this.isCurrentPlayerKiller) return
            if (this.killerSplashShownFor === this.currentPlayer?.id) {
                this.killerSplashShownFor = null
                return
            }
            this.showBecameKillerSplash()
        },
        currentPlayer(current, previous) {
            if (current && previous && current.id !== previous.id) this.showTurnSplash()
        },
        isMatchOver(isOver) {
            if (isOver) this.showWinSplash()
        },
    },
}
</script>

<style lang="scss">
.killerBg {
    height: 100dvh;
    width: 100vw;
    background: url('@/assets/images/games/killer/killerBg.png');
    background-size: auto 100%;
}
</style>
